import { useCallback, useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import axiosInstance from '../../../../../utils/axiosInstance'
import { allowedDocumentFileTypes } from '../../../../../constants'
import { IdataSourceItem, IfileItem } from '../../../../../types/other'
import { setFilesDataSources } from '../../../ReadEditOrder/components/funcs'

type AssetType =
  | 'previewImage'
  | 'previewVideo'
  | 'previewAudio'
  | 'imageAsset'
  | 'videoAsset'
  | 'jobFile'

const deleteOrderAsset = (assetType: AssetType, assetId: string) =>
  axiosInstance.post('/order-asset-deletion', { assetType, assetId })

export function useFileManagement(order: any) {
  const [previewFiles, setPreviewFiles] = useState<IfileItem[]>([])
  const [previewIdsToDelete, setPreviewIdsToDelete] = useState<string[]>([])
  const [previewIdsToCreate, setPreviewIdsToCreate] = useState<string[]>([])
  const [previewsDataSources, setPreviewsDataSources] = useState<IdataSourceItem[]>([])

  const [assetsFiles, setAssetsFiles] = useState<IfileItem[]>([])
  const [assetsIdsToDelete, setAssetsIdsToDelete] = useState<string[]>([])
  const [assetsIdsToCreate, setAssetsIdsToCreate] = useState<string[]>([])
  const [assetsDataSources, setAssetsDataSources] = useState<IdataSourceItem[]>([])

  const [videoAssets, setVideoAssets] = useState<IfileItem[]>([])
  const [videoAssetsIdsToDelete, setVideoAssetsIdsToDelete] = useState<string[]>([])
  const [videoAssetsIdsToCreate, setVideoAssetsIdsToCreate] = useState<string[]>([])
  const [videoAssetsDataSources, setVideoAssetsDataSources] = useState<IdataSourceItem[]>([])

  const [headerImageId, setHeaderImageId] = useState<string | null>(null)
  const [hasInitializedHeaderImage, setHasInitializedHeaderImage] = useState<boolean>(false)

  const [jobFile, setJobFile] = useState<File | null>(null)
  const [existingJobFile, setExistingJobFile] = useState<any>(null)
  const [fileError, setFileError] = useState<string | null>(null)

  const deletePreviewImage = useCallback(async (id: string) => {
    await deleteOrderAsset('previewImage', id)
  }, [])

  const deletePreviewAudio = useCallback(async (id: string) => {
    await deleteOrderAsset('previewAudio', id)
  }, [])

  const deletePreviewVideo = useCallback(async (id: string) => {
    await deleteOrderAsset('previewVideo', id)
  }, [])

  const deleteAssetImage = useCallback(async (id: string) => {
    await deleteOrderAsset('imageAsset', id)
  }, [])

  const deleteFileAssetFile = useCallback(async (id: string) => {
    await deleteOrderAsset('videoAsset', id)
  }, [])

  const deleteJobFileHandler = useCallback(async (id: string) => {
    await deleteOrderAsset('jobFile', id)
  }, [])

  const handleToggleHeaderImage = useCallback((assetId: string) => {
    setHeaderImageId((prevId) => (prevId === assetId ? null : assetId))
  }, [])

  const onDropJobFile = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]
      const allowedTypes = Object.values(allowedDocumentFileTypes.document)
      if (!allowedTypes.includes(file.type)) {
        setFileError('Only PDF and Word documents are allowed for job file')
        return
      }
      setJobFile(file)
      setExistingJobFile(null)
    }
  }, [])

  const {
    acceptedFiles: acceptedJobFiles,
    getRootProps: getRootPropsJobFile,
    getInputProps: getInputPropsJobFile,
    isDragActive: isDragActiveJobFile,
  } = useDropzone({
    onDrop: onDropJobFile,
    maxFiles: 1,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
  })

  const handleDeleteAllPreviews = (setFieldValue: (field: string, value: any) => void) => {
    const idsToDelete = previewsDataSources.map((item) => item.id)
    setPreviewIdsToDelete(idsToDelete)
    setPreviewsDataSources([])
    setPreviewFiles([])
    setFieldValue('previews', null)
    setFieldValue('previewsAudio', null)
    setFieldValue('previewsVideo', null)
  }

  useEffect(() => {
    if (order) {
      setFilesDataSources(
        order,
        setPreviewsDataSources,
        setAssetsDataSources,
        setVideoAssetsDataSources
      )
      if (order.jobFile) {
        setExistingJobFile(order.jobFile)
      }
    }
  }, [order])

  useEffect(() => {
    if (assetsDataSources.length > 0 && !hasInitializedHeaderImage) {
      const headerImage = assetsDataSources.find((asset: any) => asset.isHeader)
      if (headerImage) {
        setHeaderImageId(headerImage.id)
      }
      setHasInitializedHeaderImage(true)
    }
  }, [assetsDataSources, hasInitializedHeaderImage])

  return {
    // Previews
    previewFiles,
    setPreviewFiles,
    previewIdsToDelete,
    setPreviewIdsToDelete,
    previewIdsToCreate,
    setPreviewIdsToCreate,
    previewsDataSources,
    setPreviewsDataSources,
    handleDeleteAllPreviews,
    // Image assets
    assetsFiles,
    setAssetsFiles,
    assetsIdsToDelete,
    setAssetsIdsToDelete,
    assetsIdsToCreate,
    setAssetsIdsToCreate,
    assetsDataSources,
    setAssetsDataSources,
    headerImageId,
    handleToggleHeaderImage,
    // Video assets
    videoAssets,
    setVideoAssets,
    videoAssetsIdsToDelete,
    setVideoAssetsIdsToDelete,
    videoAssetsIdsToCreate,
    setVideoAssetsIdsToCreate,
    videoAssetsDataSources,
    setVideoAssetsDataSources,
    // Job file
    jobFile,
    existingJobFile,
    setExistingJobFile,
    fileError,
    acceptedJobFiles,
    getRootPropsJobFile,
    getInputPropsJobFile,
    isDragActiveJobFile,
    deleteJobFileHandler,
    // Delete handlers for form submission
    deletePreviewImage,
    deletePreviewAudio,
    deletePreviewVideo,
    deleteAssetImage,
    deleteFileAssetFile,
  }
}
